"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";


import AttachFile from "@mui/icons-material/AttachFile";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import Add from "@mui/icons-material/Add";

import type { FileAttachment } from "../../file-tree-manager/types/tree-types"
import { uploadAttachment, downloadAttachment, deleteAttachment } from "../../api/admin"
import { formatFileSize, formatDate } from "../../file-tree-manager/utils/format-utils"

interface AttachmentManagerProps {
  nodeId?: string
  attachments: FileAttachment[]
  onAdd: (attachment: FileAttachment) => void
  onRemove: (attachmentId: string) => void
}

export const AttachmentManager: React.FC<AttachmentManagerProps> = ({ nodeId, attachments, onAdd, onRemove }) => {
  const [error, setError] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // 노드가 변경되면 에러 메시지와 파일 입력을 초기화
    setError("")
  }, [nodeId])

  // 공통 유효성 검사 + 업로드
  const validateAndUpload = async (file: File) => {
    console.log("validateAndUpload")
    const maxSize = 10 * 1024 * 1024 // 10MB
    const allowedMimeTypes = new Set(["application/pdf", "image/png"])
    const allowedExtensions = [".pdf", ".png"]
    const lowerName = (file.name || "").toLowerCase()
    const hasAllowedExt = allowedExtensions.some((ext) => lowerName.endsWith(ext))
    const hasAllowedType = allowedMimeTypes.has(file.type)

    if (!(hasAllowedExt || hasAllowedType)) {
      setError("PDF 또는 PNG 파일만 업로드할 수 있습니다.")
      return
    } else if (file.size > maxSize) {
      setError("파일 크기는 10MB를 초과할 수 없습니다.")
      return
    } else {
      setError("")
    }

    if (!nodeId) {
      setError("노드가 선택되지 않았습니다.")
      return
    }

    const newAttachment: FileAttachment = {
      id: Date.now().toString(),
      contentsId: nodeId ? parseInt(nodeId) : 0,
      fileName: file.name || "upload",
      fileSize: file.size.toString(),
      fileType: file.type || "application/octet-stream",
      uploadedAt: new Date(),
    }

    try {
      const saved = await uploadAttachment(nodeId, file, newAttachment)
      onAdd(saved)
      setError("")
    } catch (e: any) {
      setError(e?.message || "파일 업로드에 실패했습니다.")
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log("handleFileSelect")
    const files = event.target.files
    if (!files || files.length === 0) return
    await validateAndUpload(files[0])
    // 파일 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleAddClick = () => {
    fileInputRef.current?.click()
  }

  // 드래그&드롭/붙여넣기/파일 피커(FSA) 핸들러
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    const files = e.dataTransfer?.files
    if (!files || files.length === 0) return
    await validateAndUpload(files[0])
  }

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (it.kind === "file") {
        const file = it.getAsFile()
        if (file) {
          await validateAndUpload(file)
          return
        }
      }
    }
    setError("붙여넣기에서 파일을 찾지 못했습니다.")
  }

  const handlePickViaFS = async () => {
    console.log("handlePickViaFS")
    try {
      const anyWindow = window as any
      if (!("showOpenFilePicker" in anyWindow)) {
        setError("브라우저가 대체 파일 선택을 지원하지 않습니다.")
        return
      }
      const [handle] = await anyWindow.showOpenFilePicker({
        multiple: false,
        types: [
          {
            description: "PDF/PNG",
            accept: {
              "application/pdf": [".pdf"],
              "image/png": [".png"],
            },
          },
        ],
        excludeAcceptAllOption: true,
      })
      const file = await handle.getFile()
      await validateAndUpload(file)
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        setError(err?.message || "파일 선택에 실패했습니다.")
      }
    }
  }

  const getFileIcon = (fileType: string) => {
    // 파일 타입에 따른 아이콘 (실제로는 더 다양한 아이콘을 사용할 수 있음)
    return <InsertDriveFile sx={{ color: "text.secondary" }} />
  }

  const handleDownload = async (attachment: FileAttachment) => {
    try {
      const blob = await downloadAttachment(attachment.id, attachment.contentsId, attachment.fileName)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = attachment.fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setError("파일 다운로드에 실패했습니다.")
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <AttachFile sx={{ mr: 1, color: "text.secondary" }} />
        <Typography variant="subtitle2">첨부 파일:</Typography>
      </Box>

      {/* 드롭존 / 붙여넣기 영역 */}
      <Box
        sx={{
          p: 2,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
          color: "text.secondary",
          mb: 2,
          textAlign: "center",
          bgcolor: "background.default",
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onPaste={handlePaste}
      >
        <Typography variant="caption">
          파일을 복사(Ctrl+C)한 후, 여기를 클릭하고 붙여넣기(Ctrl+V)를 합니다. PDF 파일과 PNG 그림파일을 붙여넣을 수 있습니다.
        </Typography>
      </Box>

      {/* 파일 추가 버튼 */}
      <Box sx={{ mb: 2 }}>
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: "none" }} accept="application/pdf,image/png,.pdf,.png" />
        {/* <Button variant="outlined" startIcon={<Add />} onClick={handleAddClick} size="small">
          파일 첨부
        </Button>
        <Button variant="outlined" sx={{ ml: 1 }} onClick={handlePickViaFS} size="small">
          대체 파일 선택
        </Button> */}
      </Box>

      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 첨부 파일 목록 */}
      <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto" }}>
        {attachments.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            <AttachFile sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">첨부된 파일이 없습니다</Typography>
            <Typography variant="caption">파일을 첨부해보세요</Typography>
          </Box>
        ) : (
          <List dense>
            {attachments.map((attachment) => (
              <ListItem key={attachment.id}>
                <ListItemIcon>{getFileIcon(attachment.fileType)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {attachment.fileName}
                      </Typography>
                      <Chip label={formatFileSize(Number(attachment.fileSize))} size="small" variant="outlined" />
                    </Box>
                  }
                  // secondary={
                  //   <Box sx={{ mt: 0.5 }}>
                  //     <Typography variant="caption" color="text.secondary">
                  //       {formatDate(attachment.uploadedAt)}
                  //     </Typography>
                  //   </Box>
                  // }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton edge="end" size="small" onClick={() => handleDownload(attachment)}>
                      <Download />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={async () => {
                        if (!nodeId) return
                        try {
                          await deleteAttachment(attachment.id, attachment.contentsId, attachment.fileName)
                          onRemove(attachment.id)
                        } catch (e) {
                          setError("파일 삭제에 실패했습니다.")
                        }
                      }}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* 첨부 파일 수 표시 */}
      {attachments.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">
            총 {attachments.length}개의 파일이 첨부되어 있습니다
          </Typography>
          <Typography variant="caption" color="text.secondary">
            총 크기: {formatFileSize(attachments.reduce((sum, att) => sum + Number(att.fileSize), 0))}
          </Typography>
        </Box>
      )}
    </Box>
  )
}


